import { useState, useEffect } from "react";
import { Truck, Van, Motorcycle, Bicycle, Bus, MapPin, Package, CheckCircle, Clock, Lightning, User, NavigationArrow, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import ProofOfDeliveryModal from "@/components/ProofOfDeliveryModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VEHICLE_ICONS = {
  van: Van,
  motorcycle: Motorcycle,
  bicycle: Bicycle,
  bus_integrated: Bus,
};

const VEHICLE_LABELS = {
  van: "Carrinha",
  motorcycle: "Mota",
  bicycle: "Bicicleta",
  bus_integrated: "Integrado Autocarro",
};

const STATUS_COLORS = {
  active: "var(--success)",
  en_route: "#2563eb",
  offline: "var(--text-muted)",
};

function DriverStatWidget({ icon: Icon, value, label, color }) {
  return (
    <div className="bento-item p-5 card-hover">
      <div className="flex items-center justify-between mb-3">
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={22} weight="duotone" style={{ color }} />
        </div>
      </div>
      <span style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700, color: "var(--text-main)", display: "block" }}>{value}</span>
      <span className="overline">{label}</span>
    </div>
  );
}

function DriverCard({ driver, isSelected, onSelect }) {
  const VehicleIcon = VEHICLE_ICONS[driver.vehicle_type] || Truck;
  return (
    <div
      data-testid={`driver-card-${driver.id}`}
      className="bento-item p-5 cursor-pointer card-hover"
      onClick={() => onSelect(driver)}
      style={{ borderColor: isSelected ? "var(--primary)" : "var(--border-color)", borderWidth: isSelected ? 2 : 1 }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--accent-sand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <User size={20} weight="duotone" style={{ color: "var(--primary)" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.95rem" }}>{driver.name}</h4>
          <div className="flex items-center gap-2">
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLORS[driver.status] || "var(--text-muted)" }} />
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "capitalize" }}>
              {driver.status === "active" ? "Ativo" : driver.status === "en_route" ? "Em rota" : "Offline"}
            </span>
          </div>
        </div>
        <VehicleIcon size={20} weight="duotone" style={{ color: "var(--primary)" }} />
      </div>

      <div className="flex items-center gap-2 mb-2" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
        <MapPin size={14} /> Zona: {driver.zone}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div style={{ textAlign: "center", padding: "6px 0", borderRadius: 8, background: "rgba(37,99,235,0.06)" }}>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", color: "#2563eb", display: "block" }}>{driver.active_deliveries}</span>
          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Ativas</span>
        </div>
        <div style={{ textAlign: "center", padding: "6px 0", borderRadius: 8, background: "rgba(62,102,86,0.06)" }}>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", color: "var(--success)", display: "block" }}>{driver.completed_today}</span>
          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Hoje</span>
        </div>
      </div>
    </div>
  );
}

export default function DriverDashboard({ drivers, bags, shipments, stats, loading }) {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [podTarget, setPodTarget] = useState(null);
  const [deliveredLocal, setDeliveredLocal] = useState({});

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/driver/stats`);
        setDriverStats(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStats();
  }, []);

  const inTransitShipments = shipments.filter(s => s.status === "in_transit" && !deliveredLocal[s.tracking_code]);

  return (
    <div data-testid="driver-dashboard" className="pt-20 px-6 pb-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 4 }}>Dashboard</span>
        <h1 style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700 }}>Motoristas e Parceiros</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Gestao de motoristas e entregas ativas</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <DriverStatWidget icon={Truck} value={driverStats?.total_drivers || drivers.length} label="Total motoristas" color="var(--primary)" />
        <DriverStatWidget icon={Lightning} value={driverStats?.total_active_deliveries || 0} label="Entregas ativas" color="var(--accent)" />
        <DriverStatWidget icon={CheckCircle} value={driverStats?.total_completed_today || 0} label="Completadas hoje" color="var(--success)" />
        <DriverStatWidget icon={Package} value={stats?.active_bags || 0} label="Bolsas em circulacao" color="#2563eb" />
      </div>

      {/* Vehicle distribution */}
      {driverStats && (
        <div className="bento-item p-5 mb-6" data-testid="vehicle-distribution">
          <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.95rem", marginBottom: 12 }}>Distribuicao por veiculo</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(driverStats.drivers_by_vehicle || {}).map(([type, count]) => {
              const Icon = VEHICLE_ICONS[type] || Truck;
              return (
                <div key={type} className="flex items-center gap-2" style={{ padding: "8px 16px", borderRadius: 10, background: "var(--bg)" }}>
                  <Icon size={18} weight="duotone" style={{ color: "var(--primary)" }} />
                  <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{count}</span>
                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{VEHICLE_LABELS[type] || type}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Drivers list */}
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-3" style={{ maxHeight: "calc(100vh - 420px)", overflowY: "auto", paddingRight: 4 }}>
            {loading ? (
              <div className="bento-item p-8 text-center"><Clock size={24} style={{ color: "var(--text-muted)", margin: "0 auto" }} /></div>
            ) : drivers.length === 0 ? (
              <div className="bento-item p-8 text-center"><Truck size={32} style={{ color: "var(--text-muted)", margin: "0 auto" }} /><p style={{ color: "var(--text-muted)", marginTop: 8 }}>Nenhum motorista encontrado</p></div>
            ) : (
              drivers.map(d => (
                <DriverCard key={d.id} driver={d} isSelected={selectedDriver?.id === d.id} onSelect={setSelectedDriver} />
              ))
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {selectedDriver ? (
            <div className="animate-fade-in" data-testid="driver-detail">
              {/* Driver info */}
              <div className="bento-item p-6 mb-4">
                <div className="flex items-center gap-4 mb-5">
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--accent-sand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <User size={28} weight="duotone" style={{ color: "var(--primary)" }} />
                  </div>
                  <div>
                    <h2 style={{ fontFamily: "Outfit", fontSize: "1.4rem", fontWeight: 700 }}>{selectedDriver.name}</h2>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLORS[selectedDriver.status] }} />
                        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                          {selectedDriver.status === "active" ? "Ativo" : "Em rota"}
                        </span>
                      </div>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>|</span>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{VEHICLE_LABELS[selectedDriver.vehicle_type]}</span>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>|</span>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Zona: {selectedDriver.zone}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div style={{ textAlign: "center", padding: 14, borderRadius: 12, background: "rgba(37,99,235,0.06)" }}>
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.8rem", color: "#2563eb" }}>{selectedDriver.active_deliveries}</span>
                    <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", marginTop: 2 }}>Entregas ativas</p>
                  </div>
                  <div style={{ textAlign: "center", padding: 14, borderRadius: 12, background: "rgba(62,102,86,0.06)" }}>
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.8rem", color: "var(--success)" }}>{selectedDriver.completed_today}</span>
                    <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", marginTop: 2 }}>Completadas hoje</p>
                  </div>
                </div>
              </div>

              {/* Current Route */}
              {selectedDriver.current_route && selectedDriver.current_route.length > 0 && (
                <div className="bento-item p-6 mb-4" data-testid="driver-route">
                  <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 12 }}>
                    <NavigationArrow size={18} weight="duotone" style={{ display: "inline", marginRight: 6, color: "var(--primary)" }} />
                    Rota atual
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedDriver.current_route.map((stop, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div style={{ padding: "8px 16px", borderRadius: 10, background: i === 0 ? "var(--primary)" : "var(--bg)", color: i === 0 ? "white" : "var(--text-main)", fontSize: "0.85rem", fontWeight: 600, fontFamily: "Manrope" }}>
                          <MapPin size={14} weight={i === 0 ? "fill" : "regular"} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
                          {stop}
                        </div>
                        {i < selectedDriver.current_route.length - 1 && (
                          <ArrowRight size={14} style={{ color: "var(--text-muted)" }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active shipments */}
              <div className="bento-item p-6">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 12 }}>Encomendas em transito na zona</h3>
                {inTransitShipments.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {inTransitShipments.map(s => (
                      <div key={s.id} style={{ padding: "12px 16px", borderRadius: 10, background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="mono" style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--primary)" }}>{s.tracking_code}</span>
                            <span className="bag-code" style={{ fontSize: "0.75rem" }}>{s.bag_code}</span>
                          </div>
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{s.pickup_point} → {s.delivery_point}</span>
                        </div>
                        <button
                          data-testid={`driver-deliver-${s.tracking_code}`}
                          onClick={() => setPodTarget(s.tracking_code)}
                          className="flex items-center gap-1"
                          style={{ padding: "7px 14px", borderRadius: 9999, border: "none", background: "var(--success)", color: "white", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}
                        >
                          <CheckCircle size={14} weight="fill" /> Entregar
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Sem encomendas em transito neste momento</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bento-item p-12 text-center animate-fade-in">
              <Truck size={48} weight="duotone" style={{ color: "var(--border-color)", margin: "0 auto 12px" }} />
              <h3 style={{ fontFamily: "Outfit", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>Selecione um motorista</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Clique num motorista para ver detalhes e rota</p>
            </div>
          )}
        </div>
      </div>

      {podTarget && (
        <ProofOfDeliveryModal
          trackingCode={podTarget}
          onClose={() => setPodTarget(null)}
          onDone={() => {
            setDeliveredLocal((prev) => ({ ...prev, [podTarget]: true }));
            setPodTarget(null);
          }}
        />
      )}
    </div>
  );
}
